import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecentActivity = ({ activities = [], className = "" }) => {
  const mockActivities = [
    {
      id: 1,
      type: 'symptom_log',
      patientName: 'Sarah Johnson',
      patientId: 'P001',
      action: 'logged symptoms',
      severity: 7,
      timestamp: new Date(Date.now() - 1800000), // 30 minutes ago
      details: 'Headache, fatigue, difficulty concentrating'
    },
    {
      id: 2,
      type: 'cognitive_test',
      patientName: 'Michael Chen',
      patientId: 'P002',
      action: 'completed cognitive assessment',
      score: 85,
      timestamp: new Date(Date.now() - 3600000), // 1 hour ago
      details: 'Reaction time test - improved performance'
    },
    {
      id: 3,
      type: 'alert',
      patientName: 'Emma Rodriguez',
      patientId: 'P003',
      action: 'triggered high-risk alert',
      severity: 9,
      timestamp: new Date(Date.now() - 7200000), // 2 hours ago
      details: 'Severe symptom spike detected'
    },
    {
      id: 4,
      type: 'ai_summary',
      patientName: 'David Thompson',
      patientId: 'P004',
      action: 'AI summary generated',
      timestamp: new Date(Date.now() - 10800000), // 3 hours ago
      details: 'Trend analysis completed successfully'
    },
    {
      id: 5,
      type: 'symptom_log',
      patientName: 'Lisa Wang',
      patientId: 'P005',
      action: 'logged symptoms',
      severity: 3,
      timestamp: new Date(Date.now() - 14400000), // 4 hours ago
      details: 'Mild symptoms, stable condition'
    }
  ];

  const activityList = activities?.length > 0 ? activities : mockActivities;

  const getActivityIcon = (type) => {
    switch (type) {
      case 'symptom_log': return 'FileText';
      case 'cognitive_test': return 'Brain';
      case 'alert': return 'AlertTriangle';
      case 'ai_summary': return 'Sparkles';
      default: return 'Activity';
    }
  };

  const getActivityColor = (type, severity) => {
    switch (type) {
      case 'symptom_log': 
        if (severity >= 7) return 'text-error bg-error/10';
        if (severity >= 4) return 'text-warning bg-warning/10';
        return 'text-success bg-success/10';
      case 'cognitive_test': return 'text-primary bg-primary/10';
      case 'alert': return 'text-error bg-error/10';
      case 'ai_summary': return 'text-accent bg-accent/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now - timestamp) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const formatActivityText = (activity) => {
    switch (activity?.type) {
      case 'symptom_log':
        return `${activity?.action} (severity: ${activity?.severity}/10)`;
      case 'cognitive_test':
        return `${activity?.action} (score: ${activity?.score}%)`;
      default:
        return activity?.action;
    }
  };

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Recent Activity</h2>
            <p className="text-sm text-muted-foreground">Latest patient interactions and updates</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
          >
            Refresh
          </Button>
        </div>
      </div>
      <div className="divide-y divide-border">
        {activityList?.map((activity) => (
          <div key={activity?.id} className="p-6 hover:bg-muted/30 transition-colors">
            <div className="flex items-start space-x-4">
              {/* Activity Icon */}
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getActivityColor(activity?.type, activity?.severity)}`}>
                <Icon 
                  name={getActivityIcon(activity?.type)} 
                  size={18} 
                  color="currentColor"
                />
              </div>

              {/* Activity Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-foreground">{activity?.patientName}</span>
                    <span className="text-xs text-muted-foreground px-2 py-1 bg-muted rounded">
                      {activity?.patientId}
                    </span>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {formatTimeAgo(activity?.timestamp)}
                  </span>
                </div>

                <div className="mb-2">
                  <p className="text-sm text-foreground">
                    {formatActivityText(activity)}
                  </p>
                  {activity?.details && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {activity?.details}
                    </p>
                  )}
                </div>

                {/* Activity-specific indicators */}
                {activity?.type === 'symptom_log' && activity?.severity >= 7 && (
                  <div className="flex items-center space-x-2 mt-2">
                    <Icon name="AlertCircle" size={14} color="var(--color-error)" />
                    <span className="text-xs text-error font-medium">High severity alert</span>
                  </div>
                )}

                {activity?.type === 'cognitive_test' && activity?.score >= 90 && (
                  <div className="flex items-center space-x-2 mt-2">
                    <Icon name="TrendingUp" size={14} color="var(--color-success)" />
                    <span className="text-xs text-success font-medium">Excellent performance</span>
                  </div>
                )}
              </div>

              {/* Quick Action Button */}
              <div className="flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="ExternalLink"
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* View All Activities */}
      <div className="p-6 border-t border-border">
        <Button
          variant="outline"
          fullWidth
          iconName="ArrowRight"
          iconPosition="right"
        >
          View All Activities
        </Button>
      </div>
      {/* Empty State */}
      {activityList?.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="Activity" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No recent activity</h3>
          <p className="text-muted-foreground">
            Patient activities will appear here as they interact with the system.
          </p>
        </div>
      )}
    </div>
  );
};

export default RecentActivity;